import ProtectedRoute from 'components/ProtectedRoute'
import Spinner from 'components/Spinner'
import useFlag from 'hooks/useFlag'
import useFlagsReady from 'hooks/useFlagsReady'
import { buildTranslate } from 'locales'
import React from 'react'
import { Redirect, Switch } from 'react-router-dom'
import OperationsCenterLocations from 'scenes/OperationsCenter/Locations'
import LocationDetail from 'scenes/OperationsCenter/Locations/Detail'
import Landing from './Landing'

const t = buildTranslate('locations')

// `/locations` is the canonical route for the Consolidated Locations page.
// Phase 1 disposition: legacy Landing remains the source of truth.
// Phase 2 (operations-center flag on): the new OC scene takes over the same
// URL so bookmarks, profile-menu links, and direct typing all land in the
// same place across the flag transition. ProtectedRoute keeps the existing
// `access` Locations gate identical in both branches so no permission
// regression for users with view-only access.
const Locations = () => {
  const operationsCenterEnabled = useFlag('operations-center', false)
  const areFlagsReady = useFlagsReady()
  const Component = operationsCenterEnabled
    ? OperationsCenterLocations
    : Landing

  // Without this, a remount (e.g. browser-back into /locations/:locationId)
  // reads useFlag's loading-default false and the redirect below fires on it.
  if (!areFlagsReady) {
    return (
      <div role="status" aria-live="polite" aria-label={t('loading')}>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }

  return (
    <Switch>
      {operationsCenterEnabled && (
        <ProtectedRoute
          redirect="/"
          accessType="access"
          resource="Locations"
          exact
          path="/locations/:locationId"
          component={LocationDetail}
        />
      )}
      <ProtectedRoute
        redirect="/"
        accessType="access"
        resource="Locations"
        exact
        path="/locations"
        component={Component}
      />
      {/* Flag-off fallthrough: bookmarked Detail URLs and live
        kill-switch flips (`/locations/123` while the user is on the
        Phase 2 surface) would otherwise render a blank page. Redirect
        back to the list so the URL degrades gracefully. */}
      {!operationsCenterEnabled && (
        <Redirect from="/locations/:locationId" to="/locations" />
      )}
    </Switch>
  )
}

export default Locations
